import { Users } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { cn } from '../../lib/utils';
import { formatNumber, formatBytes, formatResponseTime } from '../../constants/nginx-sites';
import { NginxSiteDetail } from '../../types';

interface KpiRowProps {
  site: NginxSiteDetail;
}

export function KpiRow({ site }: KpiRowProps) {
  const errorCount =
    site.statusBreakdown?.filter((s) => s.status >= 400).reduce((sum, s) => sum + s.count, 0) || 0;
  const errorRate = site.totalRequests > 0 ? (errorCount / site.totalRequests) * 100 : 0;

  const errorRateColor =
    errorRate < 2
      ? 'text-green-600 dark:text-green-400'
      : errorRate <= 5
        ? 'text-amber-500 dark:text-amber-400'
        : 'text-red-600 dark:text-red-400';

  const avgResponseTime = site.responseTimeStats?.avg ?? null;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {/* Visitantes Únicos — hero card */}
        <Card className="border-primary/20">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Visitantes Únicos</p>
                <p className="text-3xl font-bold tracking-tight">{formatNumber(site.uniqueVisitors)}</p>
              </div>
              <Users className="h-5 w-5 text-primary mt-1 shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* Page Views */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-muted-foreground mb-1">Page Views</p>
            <p className="text-2xl font-semibold">{formatNumber(site.totalPageViews)}</p>
          </CardContent>
        </Card>

        {/* Requisições — secondary */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-muted-foreground/70 mb-1">Requisições</p>
            <p className="text-2xl font-semibold text-muted-foreground">
              {formatNumber(site.totalRequests)}
            </p>
          </CardContent>
        </Card>

        {/* Taxa de Erro — conditional color */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-muted-foreground mb-1">Taxa de Erro</p>
            <p className={cn('text-2xl font-semibold', errorRateColor)}>{errorRate.toFixed(1)}%</p>
          </CardContent>
        </Card>

        {/* Tempo de Resposta Médio */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-xs font-medium text-muted-foreground mb-1">Tempo de Resposta</p>
            <p className="text-2xl font-semibold">
              {avgResponseTime != null ? formatResponseTime(avgResponseTime) : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary strip */}
      <p className="text-xs text-muted-foreground px-1">
        Tráfego: {formatBytes(site.totalBytes)} | Bots: {formatNumber(site.botRequests)}
      </p>
    </div>
  );
}
